import {
  IconAndNameEditorMenu,
  IconButton,
  Menu,
  MenuItem,
  notify,
  useConfirmModal,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useI18n } from '@affine/i18n';
import { MoreHorizontalIcon, TemplateIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import { AccountTemplateService } from '../services/account-template';
import { AccountTemplateTransferService } from '../services/account-template-transfer';
import type { AccountTemplateMeta } from '../type';
import * as styles from './styles.css';

const stopPointer = (e: React.PointerEvent) => e.stopPropagation();
const stopClick = (e: React.MouseEvent) => e.stopPropagation();

const AccountTemplateActions = ({
  template,
  republishDocId,
}: {
  template: AccountTemplateMeta;
  republishDocId?: string;
}) => {
  const t = useI18n();
  const accountTemplateService = useService(AccountTemplateService);
  const transferService = useService(AccountTemplateTransferService);
  const { openConfirmModal } = useConfirmModal();
  const [renameOpen, setRenameOpen] = useState(false);

  const handleRename = useCallback(
    (name: string) => {
      accountTemplateService.rename(template.id, name);
    },
    [accountTemplateService, template.id]
  );

  const handleRepublish = useAsyncCallback(async () => {
    if (!republishDocId) return;
    try {
      const result = await transferService.republish(
        template.id,
        republishDocId
      );
      notify.success({
        title: t['com.affine.account-template.updated-notify.title'](),
        message:
          result.skippedBlobs > 0
            ? t['com.affine.account-template.skipped-blobs.description']({
                count: String(result.skippedBlobs),
              })
            : undefined,
      });
    } catch (e) {
      console.error(e);
      notify.error({
        title: t['com.affine.account-template.failed-notify.title'](),
      });
    }
  }, [republishDocId, transferService, template.id, t]);

  const handleDelete = useCallback(() => {
    openConfirmModal({
      title: t['com.affine.account-template.delete-confirm.title'](),
      description:
        t['com.affine.account-template.delete-confirm.description'](),
      confirmText: t['com.affine.account-template.delete'](),
      confirmButtonOptions: { variant: 'error' },
      onConfirm: () => accountTemplateService.remove(template.id),
    });
  }, [openConfirmModal, t, accountTemplateService, template.id]);

  return (
    <>
      <Menu
        items={
          <>
            <MenuItem onClick={() => setRenameOpen(true)}>
              {t['com.affine.account-template.rename']()}
            </MenuItem>
            {republishDocId ? (
              <MenuItem onClick={handleRepublish}>
                {t['com.affine.account-template.republish']()}
              </MenuItem>
            ) : null}
            <MenuItem type="danger" onClick={handleDelete}>
              {t['com.affine.account-template.delete']()}
            </MenuItem>
          </>
        }
      >
        <IconButton
          size="16"
          className={styles.itemActions}
          data-testid={`account-template-actions-${template.id}`}
          onPointerDown={stopPointer}
          onClick={stopClick}
        >
          <MoreHorizontalIcon />
        </IconButton>
      </Menu>
      <IconAndNameEditorMenu
        open={renameOpen}
        onOpenChange={setRenameOpen}
        name={template.name}
        onNameChange={handleRename}
        inputTestId="account-template-rename-input"
      >
        {null}
      </IconAndNameEditorMenu>
    </>
  );
};

export interface AccountTemplateSectionProps {
  onSelect: (templateId: string) => void;
  /** when set, the actions menu can re-publish this doc into the template */
  republishDocId?: string;
}

/**
 * The "shared" half of the template picker: templates stored on the account
 * and therefore visible in every project of this user.
 */
export const AccountTemplateSection = ({
  onSelect,
  republishDocId,
}: AccountTemplateSectionProps) => {
  const t = useI18n();
  const accountTemplateService = useService(AccountTemplateService);
  const templates = useLiveData(accountTemplateService.list.templates$);

  if (templates.length === 0) {
    return (
      <div className={styles.empty} data-testid="account-template-empty">
        <span className={styles.emptyText}>
          {t['com.affine.template-list.account.empty']()}
        </span>
      </div>
    );
  }

  return (
    <>
      {templates.map(template => (
        <MenuItem
          key={template.id}
          prefixIcon={<TemplateIcon />}
          onClick={() => onSelect(template.id)}
          suffix={
            <AccountTemplateActions
              template={template}
              republishDocId={republishDocId}
            />
          }
          data-testid={`account-template-item-${template.id}`}
        >
          {template.name}
        </MenuItem>
      ))}
    </>
  );
};
